import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  gamesQuery,
  getGamesSessions,
  scenarioLevelsQuery,
  scoresQuery
} from './api';

import { popupMessagesSummary, gameQuestionsLimit } from './data';

import PerformanceRankingInfo from '../UI/performanceRankingInfo';
import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Summary from '../UI/game/summary';

const calculateScoreStats = (scores) => {
  if (!scores || !scores.length) return { points: 0, accuracy: 0, averageTime: 0 };
  
  const calculations = scores.reduce((acc, scoresStat) => {
    return {
      totalPoints: acc.totalPoints + scoresStat.total_points,
      totalAccuracy: acc.totalAccuracy + scoresStat.total_accuracy,
      totalAverageTime: acc.totalAverageTime + calculateTimeInSeconds(scoresStat.average_time),
      count: acc.count + 1
    };
  }, { totalPoints: 0, totalAccuracy: 0, totalAverageTime: 0, count: 0 });

  
  const totalPoints = calculations.totalPoints;
  const averageAccuracy = calculations.count > 0 ? (calculations.totalAccuracy / calculations.count) : 0;
  const averageAverageTime = calculations.count > 0 ? (calculations.totalAverageTime / calculations.count).toFixed(2) : 0;

  return {points: totalPoints, accuracy: averageAccuracy, averageTime: averageAverageTime};
};

export default function ThePrinciplesSummary() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentThePrinciplesGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentThePrinciplesGameId();

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);
  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);
  const [firstAttemptGameSessionId, setFirstAttemptGameSessionId] = useState(0);

  const [scenarioId, setScenarioId] = useState(0);
  const [rank, setRank] = useState('');

  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [firstAttemptPoints, setFirstAttemptPoints] = useState(0);
  const [firstAttemptAccuracy, setFirstAttemptAccuracy] = useState(0);
  const [firstAttemptAverageTime, setFirstAttemptAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummary'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: scores } = useQuery(scoresQuery({ gameId, userId: user?.id, gameSessionId: currentGameSessionId }));
  const { data: firstAttemptScores } = useQuery(scoresQuery({ gameId, userId: user?.id, gameSessionId: firstAttemptGameSessionId }));

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }

      if(currentGameSessions.length > 1) {
        const firstAttemptGameSession = currentGameSessions.find(session => session.attempt === 1);
        setFirstAttemptGameSessionId(firstAttemptGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('The Principles') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);
  
  useEffect(() => {
    if(scores && scenarioLevels?.length) {
      const calculatedScores = calculateScoreStats(scores);

      setPoints(calculatedScores.points);
      setAccuracy(calculatedScores.accuracy);
      setAverageTime(calculatedScores.averageTime);
      
      const scenarioLevelsQuestions = scenarioLevels.reduce((acc, scenarioLevel) => acc + scenarioLevel.transactions.length, 0);
      const totalQuestions = Math.min(gameQuestionsLimit, scenarioLevelsQuestions)

      const calculatedRank = calculatedScores.points >= 90 * totalQuestions
        ? 'Master'
        : calculatedScores.points >= 70 * totalQuestions
          ? 'Warrior'
          : calculatedScores.points >= 50 * totalQuestions
            ? 'Newbie'
            : 'Noob';

      setRank(calculatedRank);
    }
  }, [scores, scenarioLevels]);
  
  useEffect(() => {
    if(firstAttemptScores) {
      const calculatedScores = calculateScoreStats(firstAttemptScores);

      setFirstAttemptPoints(calculatedScores.points);
      setFirstAttemptAccuracy(calculatedScores.accuracy);
      setFirstAttemptAverageTime(calculatedScores.averageTime);
    }
  }, [firstAttemptScores]);

  useEffect(() => {
    if (scores) {
      setDataLoaded(true);
    }
  }, [scores]);

  const handlePopupMessagesNextButtonClick = () => {
    navigate('/the-principles/summary/breakdown');
  }

  const popupMessagesFormatted = useMemo(() => {
    let formattedMessages = currentGameSessionAttempt > 1 
      ? popupMessagesSummary
      : [
          {
            ...popupMessagesSummary[0],
            text: 'Congratulations on completing the exercise!<br><br>' + popupMessagesSummary[0].text
          },
          ...popupMessagesSummary.slice(1)
        ];
    
    if(formattedMessages) {
      formattedMessages = formattedMessages.map(message => ({
        ...message,
        text: message.text.replace('{accuracy}', `${(accuracy * 100).toFixed(2)}%`)
          .replace('{averageTime}', `${averageTime} seconds`)
          .replace('{points}', points.toLocaleString('en-US'))
          .replace('{rank}', rank)
      }));
    }
    
    return formattedMessages;
  }, [currentGameSessionAttempt, accuracy, averageTime, points, rank]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }
  
  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessagesFormatted}
              buttonText="Next"
              messageSize="small"
              onButtonClick={handlePopupMessagesNextButtonClick}
            />
            <Summary 
              rank={rank}
              points={points}
              firstAttemptPoints={firstAttemptPoints}
              lines={[
                {label: 'Accuracy', value: accuracy, firstAttemptValue: firstAttemptAccuracy},
                {label: 'Average Time', value: averageTime, firstAttemptValue: firstAttemptAverageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
